import { useLingui } from '@lingui/react/macro';
import { Zap } from 'lucide-react';
import { Image } from '@/routes/-components/image';
import { DonatingIllustration } from '@worldcoo/ui/custom/donating-illustration';
import { Eyebrow } from '../primitives';
import { Expand, ExpandGroup, EXPAND_STEP_ROOMY } from '../expand';
import { LiveDonations } from './live-donations';

/**
 * "Tecnología que transforma compras cotidianas en impacto social" — the Solutions hero.
 *
 * Two columns on desktop: the copy on the left, and on the right a pair of square-ish media cards
 * with the live-donations odometer sitting under both. On narrow screens it becomes one column and
 * the media cards share a row, which keeps them from turning into two full-width slabs.
 *
 * Type scale is the Figma export's (56px/64px at -0.02em, 16px/150% body) — `tracking-tight` is
 * -0.025em, not the -0.02em this hand-off uses, so the value is spelled out.
 */
export function Hero() {
	const { t } = useLingui();
	return (
		<ExpandGroup step={EXPAND_STEP_ROOMY} asChild>
			<section className="mx-auto grid max-w-[1440px] items-center gap-10 px-6 pt-10 pb-16 md:pb-24 lg:grid-cols-[minmax(0,646px)_minmax(0,1fr)] lg:gap-16 lg:pt-16 xl:px-10">
				<div className="flex flex-col items-start gap-6">
					<Eyebrow icon={<Zap className="text-brand-strong size-4 fill-current" />}>{t`Solutions`}</Eyebrow>
					<div className="flex flex-col gap-2">
						<h1 className="text-4xl leading-[1.143] font-medium tracking-[-0.02em] text-balance md:text-[3.5rem]">
							{t`Technology that turns everyday purchases into social impact`}
						</h1>
						<p className="text-ink max-w-[559px] text-base leading-[1.5]">
							{t`Since 2013, at Worldcoo we build fundraising tools so that companies, their customers and their employees can finance social projects easily, comfortably and with full transparency.`}
						</p>
					</div>
				</div>

				<div className="flex flex-col gap-6">
					{/* `radius`: these two are the redesign's one 28px card rather than the `rounded-3xl` the
				    sweep assumes, and the opening clip has to match the box it is clipping. */}
					<div className="grid grid-cols-[1.34fr_1fr] gap-6">
						<Expand index={0} radius="1.75rem" asChild>
							<div className="bg-sand aspect-[337/323] overflow-hidden rounded-[1.75rem]">
								<Image
									src="/redesign/shop-assistant-contactless.jpg"
									alt={t`A shop assistant accepting a contactless payment`}
									height={646}
									aspectRatio={337 / 323}
									objectFit="cover"
									className="h-full w-full object-cover!"
								/>
							</div>
						</Expand>
						<Expand index={1} radius="1.75rem" asChild>
							<div className="bg-sage flex aspect-[251/323] items-center justify-center overflow-hidden rounded-[1.75rem] p-2">
								{/* The drawing as the design system holds it — the same vector the donor app and the NGO
								    onboarding already render — rather than the 2MB square PNG export that used to sit
								    here and had to be blown up 25% to fill the card. Its strokes are the palette's own
								    ink, and `currentColor` is the coins, so `text-brand` is what makes them orange. */}
								<DonatingIllustration
									role="img"
									aria-label={t`Hands catching euro coins`}
									className="text-brand h-auto w-full"
								/>
							</div>
						</Expand>
					</div>

					<LiveDonations />
				</div>
			</section>
		</ExpandGroup>
	);
}
